import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { WhipLogo } from "@/components/WhipLogo";
import { Lock, Mail, ArrowLeft } from "lucide-react";

export default function Login() {
  const { user, checking, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.error || "");
  const [submitting, setSubmitting] = useState(false);

  if (!checking && user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const serverSide = !status || status >= 500 || status === 520 || typeof err.response?.data === "string";
      setError(serverSide
        ? "WHIP is briefly unavailable. Please try again in a few seconds."
        : formatApiErrorDetail(detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--whip-bg, #0b0b0f)" }}>
      <div className="w-full max-w-md">
        <Link to="/about" className="inline-flex items-center gap-1.5 text-xs text-white/45 hover:text-orange-300 transition-colors mb-6" data-testid="login-back-to-about">
          <ArrowLeft className="w-3.5 h-3.5" /> What is WHIP?
        </Link>

        <div className="whip-surface rounded-2xl border border-white/10 p-8">
          <div className="flex flex-col items-center mb-8">
            <WhipLogo size={34} />
            <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/40 mt-3">
              Legislative Intelligence
            </p>
          </div>

          {error && (
            <div className="mb-5 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider text-white/40 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-white/15 bg-white/[0.03] pl-10 pr-4 py-2.5 text-sm text-white placeholder-white/25 outline-none focus:border-orange-500/60 transition-colors"
                  placeholder="you@example.com" data-testid="login-email-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider text-white/40 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-white/15 bg-white/[0.03] pl-10 pr-4 py-2.5 text-sm text-white placeholder-white/25 outline-none focus:border-orange-500/60 transition-colors"
                  placeholder="••••••••" data-testid="login-password-input"
                />
              </div>
            </div>
            <button
              type="submit" disabled={submitting}
              className="w-full rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 py-2.5 text-sm font-bold text-white hover:from-orange-500 hover:to-orange-400 transition-colors disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <p className="text-center text-xs text-white/40 mt-4">
            Approved but have not set a password yet?{" "}
            <Link to="/setup-link" className="text-orange-300/80 hover:text-orange-200 underline-offset-2 hover:underline" data-testid="login-request-setup-link">Request a setup link</Link>
          </p>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] font-mono uppercase tracking-wider text-white/35">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <button
            type="button" onClick={handleGoogle}
            className="w-full rounded-lg border border-white/15 bg-white/[0.04] py-2.5 text-sm font-semibold text-white hover:bg-white/[0.08] transition-colors flex items-center justify-center gap-2.5"
            data-testid="login-google-button"
          >
            <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
              <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C40.9 35.6 44 30.3 44 24c0-1.3-.1-2.6-.4-3.9z"/>
            </svg>
            Sign in with Google
          </button>

          <p className="text-[11px] text-white/35 text-center mt-6 leading-relaxed">
            WHIP is invite-only. If you don't have access yet, ask your administrator for an invitation.
          </p>
        </div>
      </div>
    </div>
  );
}
